import React from "react";
import { createUseStyles } from "react-jss";
import { partiallyHideHash, pxToRem, setDataToClipboard } from "@/util/common";
import { PullToRefresh, InfiniteScroll } from "antd-mobile";
import InfiniteScrollContent from "../InfiniteScrollContent";
import CustomEmpty from "../Empty";
import useCustomTranslation from "@/hooks/useTranslation";
import { RecommendItem } from "@/api/type";

interface RecommendListProps {
	data: RecommendItem[];
	hasMore: boolean;
	onRefresh: () => Promise<void>;
	onLoadMore: (isRetry: boolean) => Promise<void>;
	isPullToRefresh?: boolean;
	isInside?: boolean;
}

const RecommendList: React.FC<RecommendListProps> = ({
	data,
	hasMore,
	onRefresh,
	onLoadMore,
	isPullToRefresh = true,
	isInside = false,
}) => {
	const classes = useStyles();
	const { t } = useCustomTranslation();

	const renderList = () => {
		if (!data.length) {
			return <CustomEmpty />;
		}

		return (
			<div className={classes.listContent}>
				<div className={classes.tableContainer}>
					<div className={classes.header}>
						{isInside && (
							<div className={classes.cell}>{t("Time")}</div>
						)}
						<div className={classes.cell}>{t("User")}</div>
						<div className={classes.cell}>{t("Address")}</div>
						<div className={classes.cell}>{t("MyRebate")}</div>
						<div className={classes.cell}>
							{t("DirectBetAmount")}
						</div>
						{isInside && (
							<div className={classes.cell}>{t("Status")}</div>
						)}
					</div>
					{data.map((item, index) => (
						<div key={index} className={classes.item}>
							{isInside && (
								<div className={classes.cell}>
									{item.orderUpdateTime}
								</div>
							)}
							<div className={classes.cell}>
								{item.nickname ?? "-"}
							</div>
							<div
								onClick={() =>
									setDataToClipboard(
										item.walletAddress,
										t("CopySuccess")
									)
								}
								className={classes.cell}
							>
								{partiallyHideHash(item.walletAddress, 4, 3)}
							</div>
							<div
								className={`${classes.cell} ${classes.rebate}`}
							>
								{item.commission ?? "-"}
							</div>
							<div className={classes.cell}>
								{item.effectiveWater ?? "-"}
							</div>
							{isInside && (
								<div className={classes.cell}>
									{item.status === 1
										? t("Completed")
										: t("PendingRebate")}
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		);
	};

	return (
		<div className={classes.container}>
			{isPullToRefresh ? (
				<PullToRefresh
					onRefresh={onRefresh}
					canReleaseText={t("canReleaseText")}
					completeText={t("completeText")}
					pullingText={t("pullingText")}
					refreshingText={t("refreshingText")}
				>
					{renderList()}
					{data.length > 0 && (
						<InfiniteScroll loadMore={onLoadMore} hasMore={hasMore}>
							<InfiniteScrollContent hasMore={hasMore} />
						</InfiniteScroll>
					)}
				</PullToRefresh>
			) : (
				renderList()
			)}
		</div>
	);
};

const useStyles = createUseStyles({
	container: {
		position: "relative",
		overflowX: "auto",
		background: "#1E212A",
	},
	listContent: {
		width: "100%",
		fontSize: pxToRem(12),
		fontWeight: "400",
	},
	tableContainer: {
		minWidth: "100%",
		display: "table",
		tableLayout: "fixed",
	},
	header: {
		display: "table-row",
		backgroundColor: "#181A22",
		color: "#FFFFFF",
		fontWeight: 400,
		fontFamily: "Poppins",
	},
	item: {
		display: "table-row",
		color: "#FFFFFF",
		"&:nth-child(odd)": {
			backgroundColor: "#181A22",
		},
		"&:not(:last-child)": {
			borderBottom: "1px solid #242837",
		},
	},
	cell: {
		display: "table-cell",
		minWidth: pxToRem(86),
		padding: `${pxToRem(12)} ${pxToRem(6)}`,
		textAlign: "center",
		verticalAlign: "middle",
		wordBreak: "break-word",
		whiteSpace: "normal",
	},
	rebate: {
		color: "#8BFF78",
	},
});

export default RecommendList;
